import { CompanyOwner, User } from '../types/schema';

// ─── Roles ───────────────────────────────────────────────
export enum Role {
    SUPER_ADMIN = 'super-admin',
    ADMIN = 'admin',
    FOREMAN = 'foreman',
    INSPECTOR = 'inspector',
    SERVICE_TECHNICIAN = 'service-technician',
}

// ─── Permissions ─────────────────────────────────────────
export enum Permission {
    INSPECT = 'inspect',
    VIEW_REPORTS = 'view-reports',
    UPDATE_DISCREPANCIES = 'update-discrepancies',
    VIEW_DISCREPANCIES = 'view-discrepancies',
    ARCHIVE_DISCREPANCIES = 'archive-discrepancies',
    MANAGE_EQUIPMENT = 'manage-equipment',
    MANAGE_FORMS = 'manage-forms',
    MANAGE_USERS = 'manage-users',
    VIEW_USERS = 'view-users',
    MANAGE_REGIONS = 'manage-regions',
    MANAGE_SAFETY_AND_TRAINING = 'manage-schedule',
    VIEW_PM = 'view-pm',
    MANAGE_PM = 'manage-pm',
    MANAGE_RESOURCE_LIBRARY = 'manage-resource-library',
    MANAGE_COMPANY_RESOURCES = 'manage-company-resources',
    VIEW_RESOURCES = 'view-resources',
    MANAGE_COST_TRACKING = 'manage-cost-tracking',
    MANAGE_TIRE_MAINTENANCE = 'manage-tire-maintenance',
    MANAGE_SKILL_TRACKING = 'manage-skill-tracking',
    MANAGE_SETTINGS = 'manage-settings',
    MANAGE_COMPANY = 'manage-company',
}

// ─── Role → Permission mapping ───────────────────────────
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
    [Role.SUPER_ADMIN]: Object.values(Permission), // everything, always
    [Role.ADMIN]: [
        Permission.INSPECT,
        Permission.VIEW_REPORTS,
        Permission.VIEW_DISCREPANCIES,
        Permission.UPDATE_DISCREPANCIES,
        Permission.ARCHIVE_DISCREPANCIES,
        Permission.MANAGE_EQUIPMENT,
        Permission.MANAGE_FORMS,
        Permission.MANAGE_USERS,
        Permission.MANAGE_COMPANY,
        Permission.VIEW_USERS,
        Permission.MANAGE_RESOURCE_LIBRARY,
        Permission.MANAGE_COMPANY_RESOURCES,
        Permission.VIEW_RESOURCES,
        Permission.VIEW_PM,
    ],
    [Role.FOREMAN]: [
        Permission.INSPECT,
        Permission.VIEW_REPORTS,
        Permission.VIEW_DISCREPANCIES,
        Permission.UPDATE_DISCREPANCIES,
        Permission.VIEW_USERS,
        Permission.MANAGE_COMPANY_RESOURCES,
        Permission.VIEW_RESOURCES,
        Permission.VIEW_PM,
    ],
    [Role.INSPECTOR]: [Permission.INSPECT, Permission.VIEW_REPORTS, Permission.VIEW_RESOURCES, Permission.VIEW_PM],
    [Role.SERVICE_TECHNICIAN]: [
        Permission.VIEW_DISCREPANCIES,
        Permission.UPDATE_DISCREPANCIES,
        Permission.ARCHIVE_DISCREPANCIES,
        Permission.VIEW_RESOURCES,
        Permission.VIEW_PM,
    ],
};

/**
 * Normalize stored role strings ("Admin", "super_admin", "owner", etc.) to a Role enum value.
 */
export const resolveRole = (role?: string | null): Role | null => {
    if (!role) return null;

    const normalized = role
        .trim()
        .toLowerCase()
        .replace(/[_\s]+/g, '-');

    if (
        normalized === 'owner' ||
        normalized === 'company-owner' ||
        normalized === 'company-admin' ||
        normalized === 'superadmin' ||
        normalized === 'super-admin'
    ) {
        return Role.SUPER_ADMIN;
    }

    if (normalized === 'administrator') {
        return Role.ADMIN;
    }

    if ((Object.values(Role) as string[]).includes(normalized)) {
        return normalized as Role;
    }

    if (normalized.startsWith('admin')) return Role.ADMIN;
    if (normalized.includes('foreman') || normalized.includes('operator')) return Role.FOREMAN;
    if (normalized.includes('inspector')) return Role.INSPECTOR;
    if (normalized.includes('service') || normalized.includes('technician')) {
        return Role.SERVICE_TECHNICIAN;
    }

    return null;
};

/** Higher number = more privileged (cannot be managed by lower roles). */
const ROLE_RANK: Record<Role, number> = {
    [Role.SUPER_ADMIN]: 100,
    [Role.ADMIN]: 80,
    [Role.FOREMAN]: 60,
    [Role.INSPECTOR]: 40,
    [Role.SERVICE_TECHNICIAN]: 40,
};

export function getRoleRank(role?: string | null): number {
    const resolved = resolveRole(role);
    return resolved ? ROLE_RANK[resolved] ?? 0 : 0;
}

/** Company owner / super-admin accounts — never manageable by lower company roles. */
export function isElevatedCompanyUser(
    target: { id?: string | null; role?: string | null; email?: string | null },
    companyOwnerId?: string | null,
    companyOwnerEmail?: string | null,
): boolean {
    if (companyOwnerId && target.id === companyOwnerId) {
        return true;
    }

    const ownerEmail = companyOwnerEmail?.trim().toLowerCase();
    const targetEmail = target.email?.trim().toLowerCase();
    if (ownerEmail && targetEmail && ownerEmail === targetEmail) {
        return true;
    }

    const normalized = (target.role || '').trim().toLowerCase();
    if (normalized === 'owner') {
        return true;
    }

    return resolveRole(target.role) === Role.SUPER_ADMIN;
}

export function getCompanyOwnerEmailFromDirectory(
    directoryUsers: { id?: string | null; email?: string | null }[],
    companyOwnerId?: string | null,
): string | null {
    if (!companyOwnerId) {
        return null;
    }
    const ownerRow = directoryUsers.find((u) => u.id === companyOwnerId);
    return ownerRow?.email?.trim() || null;
}

/**
 * Whether the signed-in user may edit or delete `target` on /users.
 * - Platform BusinessAdmin → anyone
 * - CompanyOwner → anyone in the company
 * - Company Super Admin → anyone in the company (including owners)
 * - Other roles → self, or users strictly below their rank (not elevated owners/super-admins)
 */
export function canManageCompanyUser(
    viewerUser: User | null,
    viewerCompanyOwner: CompanyOwner | null,
    target: { id?: string | null; role?: string | null; email?: string | null },
    companyOwnerId?: string | null,
    companyOwnerEmail?: string | null,
    viewerBusinessAdmin?: unknown | null,
): boolean {
    if (viewerBusinessAdmin) {
        return true;
    }

    if (viewerCompanyOwner) {
        return true;
    }

    if (viewerUser && resolveRole(viewerUser.role) === Role.SUPER_ADMIN) {
        return true;
    }

    if (!viewerUser?.id || !target.id) {
        return false;
    }

    if (viewerUser.id === target.id) {
        return true;
    }

    if (isElevatedCompanyUser(target, companyOwnerId, companyOwnerEmail)) {
        return false;
    }

    const viewerRank = getRoleRank(viewerUser.role);
    const targetRank = getRoleRank(target.role);

    return targetRank < viewerRank;
}

// ─── Permission check (single source of truth) ──────────
/**
 * Returns true if the user has the given permission.
 *  1. BusinessAdmin → always true (full platform access).
 *  2. CompanyOwner → full company admin access (account owner).
 *  3. User → role-based check via ROLE_PERMISSIONS map (with role normalization).
 *  4. Explicit per-user overrides (edge-case grants beyond role).
 */
export const hasPermission = (
    companyOwner: CompanyOwner | null,
    companyUser: User | null,
    permission: Permission,
    businessAdmin?: any, // should be BusinessAdmin | null | undefined
): boolean => {
    // BusinessAdmin bypass — full platform access
    if (businessAdmin) return true;

    // Company owners are company administrators with full access
    if (companyOwner) return true;

    if (!companyUser) return false;

    // Role-based permissions (normalize legacy / display role strings)
    const role = resolveRole(companyUser.role);
    const rolePerms = role ? ROLE_PERMISSIONS[role] || [] : [];
    if (rolePerms.includes(permission)) return true;

    // Explicit per-user overrides stored on the user record
    try {
        const explicit: string[] = JSON.parse(companyUser.permissions || '[]');
        return explicit.includes(permission);
    } catch {
        return false;
    }
};

// ─── BusinessAdmin helper ────────────────────────────────
/** Returns true if the given businessAdmin record is non-null (i.e. the user is a BusinessAdmin). */
export const isBusinessAdmin = (businessAdmin: any): boolean => businessAdmin != null;

// ─── UI helpers for user management ──────────────────────
export const ROLE_OPTIONS = [
    { label: 'Admin', value: Role.ADMIN },
    { label: 'Foreman', value: Role.FOREMAN },
    { label: 'Inspector', value: Role.INSPECTOR },
    { label: 'Service Technician', value: Role.SERVICE_TECHNICIAN },
];

/** Grouped permission options for the Multiselect in user management */
export const ALL_PERMISSION_OPTIONS = [
    {
        label: 'Inspector',
        options: [
            { label: 'Conduct Inspections', value: Permission.INSPECT },
            { label: 'View Reports', value: Permission.VIEW_REPORTS },
        ],
    },
    {
        label: 'Service Technician',
        options: [
            { label: 'Update Issues', value: Permission.UPDATE_DISCREPANCIES },
            { label: 'View Issues', value: Permission.VIEW_DISCREPANCIES },
            { label: 'Archive Issues', value: Permission.ARCHIVE_DISCREPANCIES },
        ],
    },
    {
        label: 'Supervisor',
        options: [
            { label: 'Manage Equipment', value: Permission.MANAGE_EQUIPMENT },
            { label: 'Manage Forms', value: Permission.MANAGE_FORMS },
            { label: 'Manage Users', value: Permission.MANAGE_USERS },
            { label: 'View Users', value: Permission.VIEW_USERS },
            { label: 'Manage Company Settings', value: Permission.MANAGE_COMPANY },
            { label: 'Manage Regions', value: Permission.MANAGE_REGIONS },
            { label: 'Add to TotalEquipIQ Business Resource Library', value: Permission.MANAGE_RESOURCE_LIBRARY },
            { label: 'Manage Company Resources', value: Permission.MANAGE_COMPANY_RESOURCES },
            { label: 'View Resources', value: Permission.VIEW_RESOURCES },
            { label: 'Manage Settings', value: Permission.MANAGE_SETTINGS },
        ],
    },
    {
        label: 'Modules',
        options: [
            { label: 'Manage Safety & Training', value: Permission.MANAGE_SAFETY_AND_TRAINING },
            { label: 'View PM', value: Permission.VIEW_PM },
            { label: 'Manage PM', value: Permission.MANAGE_PM },
            { label: 'Manage Cost Tracking', value: Permission.MANAGE_COST_TRACKING },
            { label: 'Manage Tire Maintenance', value: Permission.MANAGE_TIRE_MAINTENANCE },
            { label: 'Manage Skill Tracking', value: Permission.MANAGE_SKILL_TRACKING },
        ],
    },
];

export const DEFAULT_USER_PERMISSION_OPTIONS = [
    { label: 'Conduct Inspections', value: Permission.INSPECT },
    { label: 'View Reports', value: Permission.VIEW_REPORTS },
];

export const DEFAULT_ADMIN_PERMISSION_OPTIONS = ALL_PERMISSION_OPTIONS.flatMap((group) => group.options);
